import { memo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Chip, Radio, RadioGroup, SelectItem, TmdxSelect } from '@ds';
import { AdminFormRow, AdminFormSection } from '@ui';
import { EmployeeFormValues, SelectItemType } from '@/types';
import { AffiliationType } from '@/api/apis/resources';
import { TXCenter } from '@/api/apis/facilities';
import { OpoOrganization } from '@/api/apis/opo';
import { EMPLOYEE_ROLES_SELECT_LIST_CONTRACTOR } from '../../config';
import { CONTRACTOR_AFFILIATION_PREFERENCES } from '../../types';
import { useAffiliationSelector } from './useAffiliationSelector';
import { OrganTypeSelector } from './OrganTypeSelector';
import { CertificationDatesRow } from './CertificationDatesRow';
import { AffiliationFacilityRow } from './AffiliationFacilityRow';

type RoleSpecificationSectionProps = {
  isReadOnly?: boolean;
  txCenter?: TXCenter;
  opo?: OpoOrganization;
  onTxCenterSelect?: (value?: TXCenter) => void;
  onOpoSelect?: (value?: OpoOrganization) => void;
  onAffiliationTypeChange?: (value: string) => void;
};

export const RoleSpecificationSection = memo(
  ({
    isReadOnly,
    txCenter,
    opo,
    onTxCenterSelect,
    onOpoSelect,
    onAffiliationTypeChange,
  }: RoleSpecificationSectionProps) => {
    const { control } = useFormContext<EmployeeFormValues>();
    const affiliationType = useWatch({ control, name: 'affiliationType' });

    const {
      affiliationTxCenterCode,
      affiliationTxCenterAddress,
      affiliationOpoCode,
      affiliationOpoAddress,
      isAffiliationChanged,
      handleTxCenterSelect,
      handleOpoSelect,
      handleAffiliationTypeChange,
    } = useAffiliationSelector({ txCenter, opo, onTxCenterSelect, onOpoSelect, onAffiliationTypeChange });

    return (
      <AdminFormSection title="Role Specification">
        <AdminFormRow title="Role" isRequired>
          <Controller
            name="roles"
            control={control}
            render={({ field: { value, ref, onChange, ...field }, fieldState: { invalid, error } }) => (
              <TmdxSelect
                items={EMPLOYEE_ROLES_SELECT_LIST_CONTRACTOR}
                variant="bordered"
                isMultiline
                selectionMode="multiple"
                placeholder="Select a role"
                labelPlacement="outside"
                aria-label="Role"
                isInvalid={invalid}
                isDisabled
                errorMessage={error?.message}
                selectedKeys={value}
                {...field}
                renderValue={(items) => {
                  const employeeRoles = items.map((item) => item.data) as SelectItemType[];
                  return (
                    <div className="flex flex-wrap gap-2">
                      {employeeRoles.map((item) => (
                        <Chip key={item.value}>{item.name}</Chip>
                      ))}
                    </div>
                  );
                }}
              >
                {(item) => {
                  const selectItem = item as SelectItemType;
                  return (
                    <SelectItem textValue={selectItem.name} key={selectItem.value}>
                      <div className="flex gap-2 items-center">
                        <span className="text-small">{selectItem.name}</span>
                      </div>
                    </SelectItem>
                  );
                }}
              </TmdxSelect>
            )}
          />
        </AdminFormRow>

        <OrganTypeSelector isReadOnly={isReadOnly} />

        <CertificationDatesRow isReadOnly={isReadOnly} />

        <AdminFormRow title="Affiliation" isRequired>
          <Controller
            name="affiliationType"
            control={control}
            render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <RadioGroup
                className="gap-8"
                orientation="horizontal"
                value={value}
                isInvalid={invalid}
                errorMessage={error?.message}
                onValueChange={(updatedValue) => handleAffiliationTypeChange(updatedValue, onChange)}
                {...field}
              >
                {CONTRACTOR_AFFILIATION_PREFERENCES.map((affiliation) => (
                  <Radio
                    isDisabled={isReadOnly}
                    className="w-38 max-w-none"
                    key={affiliation.value}
                    value={affiliation.value}
                    size="lg"
                  >
                    {affiliation.name}
                  </Radio>
                ))}
              </RadioGroup>
            )}
          />
        </AdminFormRow>

        {affiliationType === AffiliationType.TXCenter && (
          <AffiliationFacilityRow
            type={AffiliationType.TXCenter}
            code={affiliationTxCenterCode}
            address={affiliationTxCenterAddress}
            isAffiliationChanged={isAffiliationChanged}
            defaultName={txCenter?.name}
            isDisabled={isReadOnly}
            onTxCenterSelect={handleTxCenterSelect}
            onOpoSelect={handleOpoSelect}
          />
        )}

        {affiliationType === AffiliationType.OPO && (
          <AffiliationFacilityRow
            type={AffiliationType.OPO}
            code={affiliationOpoCode}
            address={affiliationOpoAddress}
            isAffiliationChanged={isAffiliationChanged}
            defaultName={opo?.name}
            isDisabled={isReadOnly}
            onTxCenterSelect={handleTxCenterSelect}
            onOpoSelect={handleOpoSelect}
          />
        )}
      </AdminFormSection>
    );
  },
);
